<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1000px">
    <v-card class="view-dialog-card">
      <v-toolbar class="elevation-2" color="primary">
        <v-toolbar-title class="ml-0">
          <span class="font-weight-bold white--text">{{
            $t('entities.invoices.viewEntity')
          }}</span>
        </v-toolbar-title>
        <v-spacer />
        <v-btn @click="close" icon dark>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="px-0 pb-0 view-dialog-content">
        <v-container>
          <v-card class="pb-2">
            <v-simple-table class="do-not-apply-hover" dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td>
                      {{ $t('entities.invoices.attributes.number') }}
                    </td>
                    <td colspan="2" class="font-weight-bold">
                      {{ invoice?.number || '—' }}
                    </td>
                    <td>
                      {{ $t('miscellaneous.createdAt') }}
                    </td>
                    <td colspan="3" class="font-weight-bold">
                      {{ formatDateValue(invoice?.created_at) }}
                    </td>
                  </tr>
                  <tr v-if="cart?.manual_discount_amount">
                    <td>{{ $t('miscellaneous.manualDiscount') }}</td>
                    <td colspan="2" class="font-weight-bold">
                      {{ formatDiscountPct(cart?.manual_discount_amount) }}
                    </td>
                    <td>{{ $t('miscellaneous.discountAmount') }}</td>
                    <td colspan="2" class="font-weight-bold">
                      {{
                        formatAmount(
                          cart?.total_gross_amount_undiscounted -
                            cart?.total_gross_amount,
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('miscellaneous.gross') }}</td>
                    <td colspan="2" class="font-weight-bold">
                      {{ formatGrossAmount(invoice?.total_gross_amount) }}
                    </td>
                    <td>{{ $t('entities.invoices.attributes.byUser') }}</td>
                    <td colspan="3" class="font-weight-bold">
                      {{ invoice?.seller_user_name || '—' }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('entities.invoices.attributes.toUser') }}</td>
                    <td colspan="2" class="font-weight-bold">
                      {{ getUserDisplayName(invoice?.buyer_user_id) }}
                    </td>
                    <td>{{ $t('miscellaneous.arena') }}</td>
                    <td colspan="3" class="font-weight-bold">
                      {{ arenaName || '—' }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t('entities.paymentMethods.entity') }}</td>
                    <td colspan="2" class="font-weight-bold">
                      {{ getPaymentMethodName(invoice?.payment_method_id) }}
                    </td>
                    <td>{{ $t('entities.invoices.attributes.fiscalized') }}</td>
                    <td colspan="3">
                      <v-chip
                        :color="
                          invoice.fiscalization_required ? 'red' : 'purple'
                        "
                        small
                        dark
                        pill>
                        {{ invoice.fiscalization_required ? 'No' : 'Yes' }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr v-if="invoice?.b2b_legal_name">
                    <td>{{ $t('entities.companies.entity') }}</td>
                    <td colspan="2" class="font-weight-bold">
                      {{ invoice.b2b_legal_name }}
                    </td>
                    <td>{{ $t('generalAttributes.pidn') }}</td>
                    <td colspan="3" class="font-weight-bold">
                      {{ invoice.b2b_tax_number || '—' }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
          <v-card v-if="cartItems.length > 0" class="mt-4 pb-2">
            <v-card-title class="pb-2">
              {{ $t('entities.invoices.cartItems') }}
            </v-card-title>
            <div class="cart-items-scrollable">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        {{ $t('entities.products.variant') }}
                      </th>
                      <th class="text-left">
                        {{ $t('generalAttributes.quantity') }}
                      </th>
                      <th class="text-right">
                        {{ $t('generalAttributes.price') }}
                      </th>
                      <th class="text-right">
                        {{ $t('purchaseV2.basket.total') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in cartItems" :key="item.id">
                      <td>
                        <div class="d-flex align-center">
                          <img
                            v-if="item.arena_variant_image"
                            :src="item.arena_variant_image"
                            :alt="item.arena_variant_name"
                            class="mr-3"
                            style="
                              width: 50px;
                              height: 50px;
                              object-fit: cover;
                              border-radius: 4px;
                            " />
                          <div>
                            <div class="font-weight-medium">
                              {{ item.arena_variant_name }}
                              <span
                                v-if="
                                  item.arena_variant_attributes?.type !==
                                    'coins' &&
                                  item.arena_variant_attributes?.amount
                                "
                                class="grey--text">
                                ({{ item.arena_variant_attributes.amount }})
                              </span>
                            </div>
                            <div
                              v-if="item.arena_variant_sku"
                              class="text-caption grey--text">
                              SKU: {{ item.arena_variant_sku }}
                            </div>
                            <div
                              v-if="item.arena_promotion_name"
                              class="text-caption">
                              <v-chip x-small color="primary" dark>
                                {{ item.arena_promotion_name }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{{ item.quantity }}</td>
                      <td class="text-right">
                        {{
                          formatPrice(
                            item.arena_variant_price_discounted ||
                              item.arena_variant_price,
                          )
                        }}
                      </td>
                      <td class="text-right font-weight-bold">
                        {{ formatPrice(item.total_gross_price) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card>
          <v-card v-else-if="isLoadingCart" class="mt-4 pb-2">
            <v-card-text class="text-center py-4">
              <v-progress-circular indeterminate color="primary" />
            </v-card-text>
          </v-card>
        </v-container>
      </v-card-text>
      <v-card-actions class="view-dialog-actions">
        <v-spacer />
        <v-btn @click="close" color="dark darken-1" text>
          {{ $t('miscellaneous.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import formatDate from '@/mixins/format-date'
import Cart from '@/models/shop/CartV2'

export default {
  name: 'ViewDialog',
  mixins: [formatDate],
  props: {
    value: { type: Boolean, default: false },
    invoice: { type: Object, default: null },
    arenaName: { type: String, default: '' },
    arenaId: { type: [String, Number], default: null },
    currencySymbol: { type: String, default: '' },
    paymentMethodsById: { type: Object, default: () => ({}) },
    usersById: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      cart: null,
      isLoadingCart: false,
    }
  },
  computed: {
    cartItems() {
      return this.cart?.items || []
    },
  },
  watch: {
    value(open) {
      if (open && this.invoice?.cart_id && this.arenaId) {
        this.fetchCart()
      } else if (!open) {
        this.cart = null
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false)
    },
    formatDateValue(value) {
      if (!value) return '—'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value
      return this.formatDate(parsed, true, true)
    },
    formatAmount(amount) {
      const num = Number(amount)
      if (!Number.isFinite(num)) {
        return '—'
      }
      return this.formatGrossAmount(num.toFixed(2))
    },
    formatGrossAmount(amount) {
      return amount ? `${amount} ${this.currencySymbol}`.trim() : '—'
    },
    formatDiscountPct(amount) {
      return amount ? `${amount} %`.trim() : '—'
    },
    getPaymentMethodName(id) {
      if (!id) return '/'
      return this.paymentMethodsById[id]?.name || id
    },

    getUserDisplayName(buyerUserId) {
      if (!buyerUserId) return '/'
      const user = this.usersById[buyerUserId]
      if (!user) return '/'
      if (user.identity?.name) return user.identity.name
      if (user?.name) return user?.name
      if (user.username) return user.username
      return '/'
    },
    async fetchCart() {
      if (!this.arenaId || !this.invoice?.cart_id) return
      this.isLoadingCart = true
      try {
        const response = await Cart.api.get(this.arenaId, this.invoice.cart_id)
        this.cart = new Cart(response.data)
      } catch (error) {
        console.error('Failed to fetch cart.', error)
        this.cart = null
      } finally {
        this.isLoadingCart = false
      }
    },
    formatPrice(price) {
      if (!price) return '—'
      return `${price} ${this.currencySymbol}`.trim()
    },
  },
}
</script>

<style scoped>
.view-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.view-dialog-content {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.view-dialog-actions {
  flex: 0 0 auto;
}

.cart-items-scrollable {
  max-height: 280px;
  overflow-y: auto;
}
</style>
